import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { makeFormField, isValidPostalCode, getCountryCodeByTopLevelDomain, formatPostalCode } from '@/helpers';
import { CmsImageUrls, ErrorMode } from '@/models';
import { ErrorEvent } from '@/services/analytics/events';
import { countries } from '@/constants';
@Component({
  name: 'CmgRetroCreditZipCodeEntry',
})
export default class CmgRetroCreditZipCodeEntry extends Vue {
  zipCode = makeFormField();
  isSearching = false;
  google: Promise<typeof google> | null = null;

  get countryCode(): string {
    return getCountryCodeByTopLevelDomain();
  }

  get infoImage(): CmsImageUrls {
    return this.$appStore.state.cms.images.retroCredit[this.countryCode.toLowerCase()].receiptZip;
  }

  get zipCodePlaceHolder(): string {
    return this.$t('pages.retroCredit.zipCode.placeholder') as string;
  }

  get zipCodeAriaLabel(): string {
    return this.$t('pages.retroCredit.zipCode.ariaLabel') as string;
  }

  get zipCodeErrorRequired(): string {
    return this.$t('pages.retroCredit.zipCode.errorRequired') as string;
  }

  get zipCodeErrorInvalid(): string {
    return this.$t('pages.retroCredit.zipCode.errorInvalid') as string;
  }

  get zipCodeMaxLength(): number {
    return countries[this.countryCode.toLowerCase()].validations.zipCode.maxLength;
  }

  mounted() {
    this.google = this.$services.googleMaps.getGoogleMaps();
  }

  validateZipCode() {
    const zip = this.zipCode.value;

    this.zipCode.dirty = true;
    this.zipCode.error = null;

    if (!zip) {
      this.zipCode.error = this.zipCodeErrorRequired;
      this.zipCode.errorMode = ErrorMode.PlaceHolder;
      return;
    }

    if (!isValidPostalCode(zip, this.countryCode)) {
      this.zipCode.error = this.zipCodeErrorInvalid;
      this.zipCode.errorMode = ErrorMode.BelowField;
      return;
    }
  }

  async onClickSearch() {
    this.validateZipCode();

    if (this.zipCode.error) {
      return;
    }

    //formatPostalCode currently have specific implementation based of the country
    //TODO: Changing the formatPostalCode method to allow using the configured mask value
    //to format the string and place the mask string into country object in the constants.ts file
    const postalCode = formatPostalCode(this.zipCode.value, this.countryCode);

    await this.google;

    this.isSearching = true;

    try {
      const coords = await this.$services.googleMaps.googleGeocodeSearchString(postalCode);
      if (!coords) {
        this.zipCode.error = this.zipCodeErrorInvalid;
        this.zipCode.errorMode = ErrorMode.BelowField;
        return;
      }
      //TODO Restaurant api would have an end point to accept postcode to return exact match
      //That would eliminate the need to making a call to googlemaps to get the coordinates
      //and filtering again against the postcode
      const restaurants = await this.$services.restaurant.restaurantSearch(coords);
      
      const filteredRestaurants = restaurants.filter(r => (r.address.postalCode || '').replace(/\s+/g, '').startsWith(postalCode.replace(/\s+/g,'')));
      if (!filteredRestaurants.length) {
        this.$services.analytics.send(new ErrorEvent('account', 'No restaurants found.', 'retro-credit'));
        this.zipCode.error = 'No restaurants found';
        this.zipCode.errorMode = ErrorMode.BelowField;
        Vue.nextTick(() => {
          setTimeout(() => {
            const focusNearby = document.getElementsByClassName('zip-search')[0] as HTMLElement;
            if (focusNearby) {
              focusNearby.focus();
            }
          }, 2000);
        });
        return;
      }
      this.$services.ui.nextFocus('step-num');
      this.$emit('search', filteredRestaurants);
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while searching for restaurants', undefined, e);
      throw e; // trigger the global error handler on an unexpected error.
    } finally {
      this.isSearching = false;
    }
  }

  onClickInfo() {
    this.$services.ui.setModalVisibility('retroCreditReceiptModal', true, {
      imageUrl: this.infoImage.mobile || '',
    });
  }
}
